import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Loader2,
  Users,
  Truck,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  HelpCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { serviceTitanAPI } from '../../lib/servicetitanApi';
import { usersAPI, vansAPI } from '../../lib/api';

/**
 * EntityMappingWizard - A guided, one-time wizard for mapping ServiceTitan entities
 * 
 * Features:
 * - Auto-opens when pending mappings exist
 * - Step-by-step flow: Technicians → Trucks → Complete
 * - Plain language explanations
 * - Simple dropdowns instead of tables
 * - Shows "Matched automatically" vs "Needs your input" labels
 */
export const EntityMappingWizard = ({ open, onClose, onComplete }) => {
  const [loading, setLoading] = useState(true);
  const [pendingMappings, setPendingMappings] = useState({ technicians: [], trucks: [] });
  const [users, setUsers] = useState([]);
  const [vans, setVans] = useState([]);
  
  // Wizard state
  const [step, setStep] = useState(1); // 1: Technicians, 2: Trucks, 3: Complete
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedEntityId, setSelectedEntityId] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Track what we've mapped in this session
  const [mappedTechnicians, setMappedTechnicians] = useState([]);
  const [mappedTrucks, setMappedTrucks] = useState([]);

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [mappingsRes, usersRes, vansRes] = await Promise.all([
        serviceTitanAPI.getPendingMappings(),
        usersAPI.list({ include_archived: false }),
        vansAPI.list({ include_archived: false }),
      ]);
      
      const mappings = mappingsRes.data || { technicians: [], trucks: [] };
      setPendingMappings(mappings);
      setUsers((usersRes.data || []).filter(u => u.role === 'tech'));
      setVans(vansRes.data || []);
      
      // Determine starting step based on what needs mapping
      if (mappings.technicians.length > 0) {
        setStep(1);
        setCurrentIndex(0);
      } else if (mappings.trucks.length > 0) {
        setStep(2);
        setCurrentIndex(0);
      } else {
        setStep(3); // All done!
      }
    } catch (error) {
      console.error('Failed to load mapping data:', error);
      // Don't show toast - silent failure for background load
    } finally {
      setLoading(false);
    }
  };

  const handleMap = async (mappingId, slEntityId, skip = false) => {
    try {
      setSaving(true);
      
      if (skip) {
        await serviceTitanAPI.updateMapping(mappingId, { status: 'skipped' });
      } else {
        await serviceTitanAPI.updateMapping(mappingId, { sl_entity_id: slEntityId });
      }
      
      // Track what we mapped
      if (step === 1) {
        setMappedTechnicians([...mappedTechnicians, mappingId]);
      } else if (step === 2) {
        setMappedTrucks([...mappedTrucks, mappingId]);
      }
      
      // Move to next item or next step
      moveToNext();
      setSelectedEntityId('');
    } catch (error) {
      toast.error('Failed to save mapping');
    } finally {
      setSaving(false);
    }
  };

  const moveToNext = () => {
    if (step === 1) {
      // Technicians step
      const remaining = pendingMappings.technicians.filter(
        t => !mappedTechnicians.includes(t.id)
      );
      if (currentIndex + 1 < remaining.length) {
        setCurrentIndex(currentIndex + 1);
      } else if (pendingMappings.trucks.length > 0) {
        // Move to trucks
        setStep(2);
        setCurrentIndex(0);
      } else {
        // All done
        setStep(3);
      }
    } else if (step === 2) {
      // Trucks step
      const remaining = pendingMappings.trucks.filter(
        t => !mappedTrucks.includes(t.id)
      );
      if (currentIndex + 1 < remaining.length) {
        setCurrentIndex(currentIndex + 1);
      } else {
        // All done
        setStep(3);
      }
    }
  };

  const handleComplete = () => {
    onComplete?.();
    onClose();
  };

  const getCurrentTechnician = () => {
    const remaining = pendingMappings.technicians.filter(
      t => !mappedTechnicians.includes(t.id)
    );
    return remaining[currentIndex] || null;
  };

  const getCurrentTruck = () => {
    const remaining = pendingMappings.trucks.filter(
      t => !mappedTrucks.includes(t.id)
    );
    return remaining[currentIndex] || null;
  };

  const getRemainingCount = () => {
    if (step === 1) {
      return pendingMappings.technicians.filter(
        t => !mappedTechnicians.includes(t.id)
      ).length;
    } else if (step === 2) {
      return pendingMappings.trucks.filter(
        t => !mappedTrucks.includes(t.id)
      ).length;
    }
    return 0;
  };

  const totalTechnicians = pendingMappings.technicians.length;
  const totalTrucks = pendingMappings.trucks.length;

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mb-4" />
            <p className="text-sm text-muted-foreground">Loading...</p>
          </div>
        ) : step === 3 ? (
          // Completion state
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <DialogHeader className="text-center">
              <DialogTitle className="text-xl">All Set!</DialogTitle>
              <DialogDescription className="mt-2">
                Your ServiceTitan data is now linked to Stock & Load.
                You won&apos;t need to do this again unless new technicians or trucks are added.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-6 space-y-2 text-sm text-muted-foreground">
              {mappedTechnicians.length > 0 && (
                <p>✓ {mappedTechnicians.length} technician{mappedTechnicians.length !== 1 ? 's' : ''} mapped</p>
              )}
              {mappedTrucks.length > 0 && (
                <p>✓ {mappedTrucks.length} truck{mappedTrucks.length !== 1 ? 's' : ''} mapped</p>
              )}
            </div>
            <Button className="mt-6" onClick={handleComplete}>
              Done
            </Button>
          </div>
        ) : step === 1 ? (
          // Technicians step
          <>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Users className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <DialogTitle>Match Technicians</DialogTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Step 1 of {totalTrucks > 0 ? '2' : '1'}
                  </p>
                </div>
              </div>
              <DialogDescription className="mt-3">
                We found technicians in ServiceTitan. Help us match them to your team members in Stock & Load.
              </DialogDescription>
            </DialogHeader>

            {getCurrentTechnician() && (
              <div className="py-4 space-y-4">
                {/* Progress indicator */}
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Technician {currentIndex + 1} of {getRemainingCount()}</span>
                  <Badge variant="outline" className="gap-1">
                    <HelpCircle className="w-3 h-3" />
                    Needs your input
                  </Badge>
                </div>

                {/* Current technician */}
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="font-medium text-lg">{getCurrentTechnician().st_entity_name}</p>
                  {getCurrentTechnician().st_entity_email && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Email: {getCurrentTechnician().st_entity_email}
                    </p>
                  )}
                </div>

                {/* Explanation */}
                <div className="text-sm text-muted-foreground">
                  <p>Which Stock & Load team member is this?</p>
                </div>

                {/* Dropdown */}
                <Select value={selectedEntityId} onValueChange={setSelectedEntityId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a team member..." />
                  </SelectTrigger>
                  <SelectContent>
                    {users.length === 0 ? (
                      <div className="p-3 text-sm text-muted-foreground text-center">
                        No team members found. Add technicians to your team first.
                      </div>
                    ) : (
                      users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          <div className="flex flex-col">
                            <span>{user.name}</span>
                            {user.email && (
                              <span className="text-xs text-muted-foreground">{user.email}</span>
                            )}
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="ghost"
                onClick={() => handleMap(getCurrentTechnician()?.id, null, true)}
                disabled={saving}
              >
                Skip this one
              </Button>
              <Button
                onClick={() => handleMap(getCurrentTechnician()?.id, selectedEntityId)}
                disabled={!selectedEntityId || saving}
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ArrowRight className="w-4 h-4 mr-2" />
                )}
                {getRemainingCount() === 1 && totalTrucks === 0 ? 'Finish' : 'Next'}
              </Button>
            </DialogFooter>
          </>
        ) : step === 2 ? (
          // Trucks step
          <>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                  <Truck className="w-4 h-4 text-orange-600" />
                </div>
                <div>
                  <DialogTitle>Match Trucks</DialogTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Step 2 of 2
                  </p>
                </div>
              </div>
              <DialogDescription className="mt-3">
                Now let&apos;s match the trucks from ServiceTitan to your vehicles in Stock & Load.
              </DialogDescription>
            </DialogHeader>

            {getCurrentTruck() && (
              <div className="py-4 space-y-4">
                {/* Progress indicator */}
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Truck {currentIndex + 1} of {getRemainingCount()}</span>
                  <Badge variant="outline" className="gap-1">
                    <HelpCircle className="w-3 h-3" />
                    Needs your input
                  </Badge>
                </div>

                {/* Current truck */}
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="font-medium text-lg">{getCurrentTruck().st_entity_name}</p>
                </div>

                {/* Explanation */}
                <div className="text-sm text-muted-foreground">
                  <p>Which Stock & Load vehicle is this?</p>
                </div>

                {/* Dropdown */}
                <Select value={selectedEntityId} onValueChange={setSelectedEntityId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a vehicle..." />
                  </SelectTrigger>
                  <SelectContent>
                    {vans.length === 0 ? (
                      <div className="p-3 text-sm text-muted-foreground text-center">
                        No vehicles found. Add vehicles to your fleet first.
                      </div>
                    ) : (
                      vans.map((van) => (
                        <SelectItem key={van.id} value={van.id}>
                          {van.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="ghost"
                onClick={() => handleMap(getCurrentTruck()?.id, null, true)}
                disabled={saving}
              >
                Skip this one
              </Button>
              <Button
                onClick={() => handleMap(getCurrentTruck()?.id, selectedEntityId)}
                disabled={!selectedEntityId || saving}
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ArrowRight className="w-4 h-4 mr-2" />
                )}
                {getRemainingCount() === 1 ? 'Finish' : 'Next'}
              </Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

/**
 * MappingStatusCard - Shows current mapping status with friendly messages
 */
export const MappingStatusCard = ({ pendingCount, onOpenWizard }) => {
  if (pendingCount === 0) {
    return (
      <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
        <div>
          <p className="font-medium text-green-900">All set!</p>
          <p className="text-sm text-green-700">
            Your ServiceTitan data is linked to Stock & Load.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg">
      <div className="flex items-center gap-3">
        <Sparkles className="w-5 h-5 text-amber-600 flex-shrink-0" />
        <div>
          <p className="font-medium text-amber-900">Quick setup needed</p>
          <p className="text-sm text-amber-700">
            {pendingCount} {pendingCount === 1 ? 'item needs' : 'items need'} to be matched.
            This only takes a minute.
          </p>
        </div>
      </div>
      <Button size="sm" onClick={onOpenWizard}>
        Start
        <ArrowRight className="w-4 h-4 ml-1" />
      </Button>
    </div>
  );
};

export default EntityMappingWizard;
